import React from "react";
import {
  Button,
  Segmented,
  Select,
  Input,
  Popconfirm,
  Tooltip,
  Avatar,
  Popover,
  Tag,
  Carousel,
  Divider,
  Space,
} from "antd";
import config, { POST_VARIANTS } from "../config";
import { generatePlatforms } from "../lib/helpers";
import {
  CaretRightFilled,
  ColumnHeightOutlined,
  ColumnWidthOutlined,
  LogoutOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  logout,
  resetState,
  setExportId,
  setFilename,
  setPostVariant,
  setSelectedPlatforms,
  setView,
  setZoomLevel,
  saveTemplate,
  setSelectedTemplate,
  updateTemplate,
  setShowDemo,
} from "../services/store";
import StepButton from "../lib/StepButton";
import GLOBAL_TEMPLATES from "../lib/templates";

const Mainbar = ({ handleDownload }) => {
  const {
    activePlatform,
    postVariant,
    zoomLevel,
    view,
    filename,
    exportId,
    totalExports,
    userPhoto,
    userId,
    userName,
    userEmail,
    templates = [],
    activeTemplateId,
  } = useSelector((state) => state.grid);
  const dispatch = useDispatch();
  const [name, setName] = React.useState("");
  return (
    <div className="flex flex-col gap-2 bg-gray-50 border border-r-gray-200 p-2 w-[280px] shrink-0 h-full">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-gray-600 font-bold truncate">{config.appName}</h3>
        </div>
        <Popover
          trigger="click"
          placement="bottomRight"
          content={
            <div className="w-36 min-h-8 flex flex-col items-stretch gap-2 overflow-hidden">
              <div className="text-xs text-gray-600 truncate">{userName}</div>
              {!!userEmail && (
                <div className="text-xs text-gray-600">{userEmail}</div>
              )}
              <Tag className="overflow-hidden truncate w-full mt-1">
                {userId}
              </Tag>
              <Button onClick={() => dispatch(setShowDemo(true))}>
                <CaretRightFilled />
                Demo
              </Button>
              <Popconfirm
                title="Sure?"
                placement="right"
                description={
                  <div className="max-w-[300px]">
                    This will reset all post settings to their default values.
                  </div>
                }
                onConfirm={() => dispatch(resetState())}
                okText="Yes"
                cancelText="No"
              >
                <Button className="w-full">Reset</Button>
              </Popconfirm>
              <Button type="primary" danger onClick={() => dispatch(logout())}>
                <LogoutOutlined className="mr-2" />
                Logout
              </Button>
            </div>
          }
        >
          <Avatar
            src={userPhoto}
            style={{
              backgroundColor: "#fde3cf",
              color: "#f56a00",
              cursor: "pointer",
              flexShrink: 0,
            }}
          >
            {userName?.charAt(0)?.toUpperCase()}
          </Avatar>
        </Popover>
      </div>

      <hr />

      <div className="flex flex-col items-stretch gap-2 grow">
        <div className="flex flex-col items-start gap-1">
          <label
            className="text-xs font-bold"
            onClick={() => console.log("[Templates]", templates)}
          >
            Templates
          </label>
          <Select
            placeholder="Template"
            suffixIcon={
              !!activeTemplateId && (
                <SaveOutlined
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(updateTemplate());
                  }}
                />
              )
            }
            className="w-full"
            options={[
              {
                label: "My templates",
                options: templates.map(({ id, label }) => ({
                  label,
                  value: id,
                })),
              },
              {
                label: "Global templates",
                options: GLOBAL_TEMPLATES.map(({ id, label }) => ({
                  label,
                  value: id,
                })),
              },
            ].filter((group) => group.options.length > 0)}
            value={activeTemplateId}
            onChange={(value) => {
              dispatch(setSelectedTemplate(value));
            }}
            popupRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space style={{ padding: "0 8px 4px" }}>
                  <Input
                    placeholder="Template name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      dispatch(saveTemplate(name));
                      setFilename("");
                    }}
                  >
                    Save
                  </Button>
                </Space>
              </>
            )}
          />
        </div>

        <div className="flex flex-col items-start gap-1">
          <label className="text-xs font-bold">Platform</label>
          <Select
            className="w-full"
            options={generatePlatforms("all").map(({ label, platform }) => ({
              label,
              value: platform,
            }))}
            value={activePlatform}
            onChange={(value) => {
              dispatch(setSelectedPlatforms(value));
            }}
          />
        </div>

        <div className="flex flex-col items-start gap-1">
          <label className="text-xs font-bold">Variant</label>
          <Select
            placeholder="Variant"
            className="w-full"
            options={POST_VARIANTS}
            value={postVariant}
            onChange={(variant) => dispatch(setPostVariant(variant))}
            optionRender={(option) => {
              return (
                <div className="flex flex-col items-start gap-.5">
                  <div>{option.data.label}</div>
                  <div className="text-xs text-gray-600 whitespace-break-spaces">
                    {option.data.description}
                  </div>
                </div>
              );
            }}
          />
        </div>

        <div className="flex flex-col items-start gap-1">
          <label className="text-xs font-bold">View</label>
          <Segmented
            onChange={(view) => dispatch(setView(view))}
            value={view}
            options={[
              { value: "col", icon: <ColumnHeightOutlined /> },
              { value: "row", icon: <ColumnWidthOutlined /> },
            ]}
          />
        </div>

        <div className="flex flex-col items-start gap-1">
          <label className="text-xs font-bold">Zoom</label>
          <StepButton
            value={zoomLevel}
            step={0.1}
            onChange={(value) => console.log(value)}
            type="number"
            onDecrement={() => dispatch(setZoomLevel("-"))}
            onIncrement={() => dispatch(setZoomLevel("+"))}
          />
        </div>
      </div>

      <div className="flex justify-end flex-col gap-2">
        <Carousel
          autoplay
          dotPlacement="bottom"
          effect="fade"
          arrows
          autoplaySpeed={10000}
        >
          <div className="!flex flex-col w-full items-center gap-2 h-32 justify-center bg-gray-100 rounded-lg">
            <div>Export Id</div>
            <StepButton
              value={exportId}
              step={1}
              min={1}
              onChange={(value) => dispatch(setExportId(value))}
              type="number"
            />
          </div>
          <div className="!flex flex-col w-full items-center gap-2 h-32 justify-center bg-gray-100 rounded-lg">
            <div>Total Exports</div>
            <span className="text-3xl">{totalExports}</span>
          </div>
        </Carousel>

        <Input
          placeholder="Filename"
          value={filename}
          onChange={(e) => dispatch(setFilename(e.target.value))}
          // addonAfter={<ReloadOutlined onClick={generateFileName} />}
        />

        <div className="flex gap-2">
          <Tooltip
            title={
              <div>
                <div className="text-xs font-bold">Export</div>
                <div className="text-xs text-gray-500">
                  Export the selected platforms as a PNG file.
                </div>
              </div>
            }
          >
            <Button className="grow" type="primary" onClick={handleDownload}>
              Export
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default Mainbar;
